import { CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { incrementRouterSpaceReactKey } from '~src/app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import {
  CreatedAtDetailSection,
  ModifiedAtDetailSection
} from '~src/app/content/contentDetailDialog/CommonContentDetailSections';
import { ContentDetailSection, sectionContainer } from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/app/content/contentDetailDialog/DeleteContentSection';
import {
  COLLECTION_DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/app/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedOwnerDetailSection } from '~src/app/content/contentDetailDialog/OwnerDetailSection';
import { CompositeSessionInfoSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { AppState } from '~src/app/state/AppState';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Formatters, Messages } from '~src/codegen/Localize';
import { L10n } from '~src/ts/L10n/L10n';
import { CollectionMapper } from '~src/ts/mappers/CollectionMapper';
import { Set } from '~src/ts/models/Set';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ICollectionInfo } from './ICollectionInfo';
import { CollectionsResourceQuery } from './ResourceQuery/CollectionsResourceQuery';


export interface CollectionDetailsDialogBodyOwnProps {
  readonly collection: ICollectionInfo;
}

export interface CollectionDetailsDialogBodyStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isAdminOrOwner: boolean;
}

interface CollectionDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type CollectionDetailsDialogBodyProps =
    CollectionDetailsDialogBodyOwnProps &
    CollectionDetailsDialogBodyStateProps &
    CollectionDetailsDialogBodyDispatchProps;

export function CollectionDetailsDialogBody(props: CollectionDetailsDialogBodyProps): JSX.Element {

  const setDescription = React.useCallback(async (description: string) => {
    const updatedCollectionData = { luid: props.collection.luid, description };
    const response = await new CollectionsResourceQuery().update([updatedCollectionData]);

    if (response.errors && response.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.collectionSetDescriptionError()
      });
    }

    return CollectionMapper.mapUpdateCollectionsResponseDataToIResult(response, Set.singletonForIdItem(props.collection));
  }, [props.collection]);

  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={props.collection.description}
        isActionEnabled={props.isAdminOrOwner}
        setDescription={setDescription}
        descriptionMaxLength={COLLECTION_DESCRIPTION_MAX_LENGTH}
        incrementRouterSpaceReactKey={props.incrementRouterSpaceReactKey}
      />
      <ConnectedOwnerDetailSection content={props.collection} isActionEnabled={props.isAdminOrOwner} urls={props.urls}/>
      <CollectionItemCountSection collection={props.collection} />
      <CreatedAtDetailSection date={props.collection.createdAt} />
      <ModifiedAtDetailSection date={props.collection.updatedAt} />
      {props.isAdminOrOwner &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.collectionsDetailsDelete()}
          content={props.collection}
        />
      }
    </div>
  );
}

export interface CollectionItemCountSectionProps {
  readonly collection: ICollectionInfo;
}

export function CollectionItemCountSection(props: CollectionItemCountSectionProps): JSX.Element {
  const hiddenItemCount = props.collection.totalItemCount - props.collection.permissionedItemCount;
  const detail: JSX.Element =
    <>
      {L10n.localizedCount(props.collection.totalItemCount)}
      { hiddenItemCount > 0 &&
        <CompactInlineAlertWidget
          testId={'collection-detail-count'}
          type={InlineAlertType.Info}
          style={{ marginTop: '8px'}}
        >
          {Messages.collectionsDetailsItemCountPermissionedWarning({ITEM_COUNT: hiddenItemCount, ITEM_COUNT_LOC: Formatters.formatCount(hiddenItemCount)})}
        </CompactInlineAlertWidget>
      }
    </>;

  return (
    <ContentDetailSection
      header={Messages.collectionsDetailsItemCount()}
      testId={'item-count'}
      detail={detail}
    />
  );
}

const mapDispatchToProps: MapDispatchToProps<CollectionDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<CollectionDetailsDialogBodyStateProps> = (state: AppState, ownProps: CollectionDetailsDialogBodyOwnProps) => ({
  isAdminOrOwner: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownProps.collection.owner)(state),
  urls: siteUrlsSelector(state),
});

export const ConnectedCollectionDetailsDialogBody: React.ComponentClass<CollectionDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(CollectionDetailsDialogBody);
